import { createStore } from 'zustand/vanilla';
import PlacementOrder from '@/models/PlacementOrder';
import Address from '@/models/Address';
import Airport from '@/models/Airport';
import Flight from '@/models/Flight';
import VehicleType from '@/models/VehicleType';
import Addon from '@/models/Addon';
import Passenger from '@/models/Passenger';
import LuggageCounter from '@/models/LuggageCounter';
import Coupons from '@/models/Coupons';
import { OrderType } from '@/data';

export interface GlobalState {
  placementOrder: PlacementOrder;
  coupons: Coupons | null;
}

export interface GlobalActions {
  // Order-related actions
  setOrderType: (orderType: OrderType) => void;
  setPickUpOrDropOffLocation: (address: Address) => void;
  setAirportLocation: (airport: Airport) => void;
  setEstimatedPickupTime: (time: Date | null) => void;
  setPickUpTime: (time: Date | null) => void;
  setFlightInformation: (flight: Flight | null) => void;
  setRawFlightDate: (rawFlightDate: string | null) => void;
  setRawFlightNumber: (rawFlightNumber: string) => void;
  setPassengerCount: (count: number) => void;
  setLuggageCounter: (counter: LuggageCounter) => void;
  setVehicleType: (vehicle: VehicleType | null) => void;
  setDescription: (description: string) => void;
  setAddon: (addon: Addon) => void;
  setNameSignContent: (nameSignContent: string | null) => void;
  setPassenger: (passenger: Passenger) => void;
  setExtraStops: (extraStops: (Address | null)[]) => void;
  setIsPriceTableLoading: (nextIsPriceTableLoading: boolean) => void;
  setIsExtraStopsValid: (isValid: boolean) => void;
  setRemoteAreaSurchargeId: (remoteAreaSurchargeId: string) => void;
  reset: () => void;
  setOrder: (placementOrder: PlacementOrder) => void;
  setCouponId: (couponId: string | null) => void;

  // Coupon-related actions
  setCoupons: (coupons: Coupons | null) => void;
}

export type GlobalStore = GlobalState & GlobalActions;

export const initGlobalStore = (): GlobalState => {
  return {
    placementOrder: new PlacementOrder(),
    coupons: null,
  };
};

export const defaultInitState: GlobalState = {
  placementOrder: new PlacementOrder(),
  coupons: null,
};

export const createGlobalStore = (initState: GlobalState = defaultInitState) => {
  return createStore<GlobalStore>()((set) => ({
    ...initState,

    // Order-related actions
    setOrderType: (orderType: OrderType) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setOrderType(orderType),
      })),

    setPickUpOrDropOffLocation: (address: Address) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setPickUpOrDropOffLocation(address),
      })),

    setAirportLocation: (airport: Airport) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setAirportLocation(airport),
      })),

    setEstimatedPickupTime: (time: Date | null) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setEstimatedPickupTime(time),
      })),

    setPickUpTime: (time: Date | null) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setPickUpTime(time),
      })),

    setFlightInformation: (flight: Flight | null) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setFlightInformation(flight),
      })),

    setRawFlightDate: (rawFlightDate: string | null) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setRawFlightDate(rawFlightDate),
      })),

    setRawFlightNumber: (rawFlightNumber: string) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setRawFlightNumber(rawFlightNumber),
      })),

    setPassengerCount: (count: number) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setPassengerCount(count),
      })),

    setLuggageCounter: (counter: LuggageCounter) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setLuggageCounter(counter),
      })),

    setVehicleType: (vehicle: VehicleType | null) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setVehicleType(vehicle),
      })),

    setDescription: (description: string) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setDescription(description),
      })),

    setAddon: (addon: Addon) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setAddon(addon),
      })),

    setNameSignContent: (nameSignContent: string | null) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setNameSignContent(nameSignContent),
      })),

    setPassenger: (passenger: Passenger) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setPassenger(passenger),
      })),

    setExtraStops: (extraStops: (Address | null)[]) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setExtraStops(extraStops),
      })),

    setIsPriceTableLoading: (nextIsPriceTableLoading: boolean) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setIsPriceTableLoading(nextIsPriceTableLoading),
      })),

    setIsExtraStopsValid: (isValid: boolean) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setIsExtraStopsValid(isValid),
      })),

    setRemoteAreaSurchargeId: (remoteAreaSurchargeId: string) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setRemoteAreaSurchargeId(remoteAreaSurchargeId),
      })),

    reset: () =>
      set((state) => ({
        ...state,
        placementOrder: new PlacementOrder(),
      })),

    setOrder: (placementOrder: PlacementOrder) =>
      set((state) => ({
        ...state,
        placementOrder,
      })),

    setCouponId: (couponId: string | null) =>
      set((state) => ({
        ...state,
        placementOrder: state.placementOrder.setCouponId(couponId),
      })),

    // Coupon-related actions
    setCoupons: (coupons: Coupons | null) =>
      set((state) => ({
        ...state,
        coupons,
      })),
  }));
};
